/**
 * Legacy UI Tagging Strategy
 *
 * These attributes remain for compatibility with existing components, but
 * PostHog autocapture is intentionally disabled. Business interactions must
 * use the explicit typed functions exported from events.ts.
 */

/**
 * Get a legacy data-ph attribute object for spreading into component props.
 */
export function getDataPh(tag: string): {
  'data-ph': string;
  'data-ph-capture-attribute-event_name': string;
} {
  return {
    'data-ph': tag,
    'data-ph-capture-attribute-event_name': tag,
  };
}

/**
 * Common data-ph tags organized by domain
 */
export const DataPhTags = {
  // Posts
  POST_CREATE: 'post_create',
  POST_LIKE: 'post_like',
  POST_SHARE: 'post_share',
  POST_EDIT: 'post_edit',
  POST_DELETE: 'post_delete',
  POST_REPORT: 'post_report',
  POST_BOOKMARK: 'post_bookmark',

  // Comments
  COMMENT_SUBMIT: 'comment_submit',
  COMMENT_LIKE: 'comment_like',
  COMMENT_REPLY: 'comment_reply',
  COMMENT_EDIT: 'comment_edit',
  COMMENT_DELETE: 'comment_delete',
  COMMENT_REPORT: 'comment_report',

  // Polls
  POLL_VOTE: 'poll_vote',
  POLL_VIEW_RESULTS: 'poll_view_results',

  // Q&A
  ANSWER_SUBMIT: 'answer_submit',
  ANSWER_UPVOTE: 'answer_upvote',
  ANSWER_DOWNVOTE: 'answer_downvote',
  QUESTION_ASK: 'question_ask',

  // Groups
  GROUP_JOIN: 'group_join',
  GROUP_LEAVE: 'group_leave',
  GROUP_INVITE: 'group_invite',
  GROUP_CREATE: 'group_create',

  // Organizations/Sponsors
  ORG_FOLLOW: 'org_follow',
  ORG_UNFOLLOW: 'org_unfollow',
  SPONSOR_CONTACT: 'sponsor_contact',
  SPONSOR_DEMO: 'sponsor_demo',

  // Events
  EVENT_REGISTER: 'event_register',
  EVENT_JOIN: 'event_join',
  EVENT_LEAVE: 'event_leave',
  SESSION_JOIN: 'session_join',
  BOOTH_VISIT: 'booth_visit',

  // Conference-events
  CONFERENCE_VIEW: 'conference_view',
  CONFERENCE_JOIN: 'conference_join',
  CONFERENCE_LEAVE: 'conference_leave',

  // Cart & Checkout
  CART_ADD: 'cart_add',
  CART_REMOVE: 'cart_remove',
  CART_VIEW: 'cart_view',
  CHECKOUT_START: 'checkout_start',
  CHECKOUT_COMPLETE: 'checkout_complete',
  PROMO_APPLY: 'promo_apply',
  PROMO_REMOVE: 'promo_remove',
  PAYMENT_COMPLETE: 'payment_complete',

  // Membership
  MEMBERSHIP_SELECT: 'membership_select',
  MEMBERSHIP_UPGRADE: 'membership_upgrade',
  MEMBERSHIP_CANCEL: 'membership_cancel',

  // Messages
  MESSAGE_SEND: 'message_send',
  MESSAGE_REPLY: 'message_reply',
  CONVERSATION_START: 'conversation_start',

  // Resources
  RESOURCE_DOWNLOAD: 'resource_download',
  RESOURCE_UPLOAD: 'resource_upload',
  RESOURCE_VIEW: 'resource_view',

  // Search
  SEARCH_SUBMIT: 'search_submit',
  SEARCH_FILTER: 'search_filter',
  SEARCH_RESULT: 'search_result',

  // Navigation
  NAV_HOME: 'nav_home',
  NAV_GROUPS: 'nav_groups',
  NAV_EVENTS: 'nav_events',
  NAV_MEMBERS: 'nav_members',
  NAV_MESSAGES: 'nav_messages',
  NAV_PROFILE: 'nav_profile',
  NAV_SETTINGS: 'nav_settings',
  NAV_NOTIFICATIONS: 'nav_notifications',
  NAV_CART: 'nav_cart',

  // Auth
  AUTH_LOGIN: 'auth_login',
  AUTH_LOGOUT: 'auth_logout',
  AUTH_SIGNUP: 'auth_signup',
  AUTH_FORGOT_PASSWORD: 'auth_forgot_password',

  // Profile
  PROFILE_EDIT: 'profile_edit',
  PROFILE_VIEW: 'profile_view',
  PROFILE_FOLLOW: 'profile_follow',
  PROFILE_UNFOLLOW: 'profile_unfollow',

  // Notifications
  NOTIFICATION_VIEW: 'notification_view',
  NOTIFICATION_MARK_READ: 'notification_mark_read',
  NOTIFICATION_CLEAR: 'notification_clear',

  // Registration
  REGISTRATION_VIEW: 'registration_view',
} as const;

/**
 * Type-safe helper to get a data-ph tag
 */
export type DataPhTag = typeof DataPhTags[keyof typeof DataPhTags];

/**
 * Validate that a tag follows the naming convention
 */
export function isValidDataPhTag(tag: string): boolean {
  // Must be kebab-case with at least one hyphen
  const kebabCasePattern = /^[a-z]+(-[a-z]+)+$/;
  return kebabCasePattern.test(tag);
}

/**
 * Example usage in components:
 * 
 * // Method 1: Using getDataPh helper
 * <button {...getDataPh('post-like')} onClick={handleLike}>
 *   Like
 * </button>
 * 
 * // Method 2: Using DataPhTags constants
 * <button data-ph-capture-attribute-event_name={DataPhTags.POST_LIKE} onClick={handleLike}>
 *   Like
 * </button>
 * 
 * // Method 3: Direct attribute
 * <button data-ph-capture-attribute-event_name="post-like" onClick={handleLike}>
 *   Like
 * </button>
 * 
 * These attributes are metadata only while autocapture is disabled.
 */
